import apiClient from './api';

export interface Review {
  id: number;
  score: number;
  content: string;
  images: string[];
  facilityName: string;
  date: string;
}

export interface ImageUploadResponse {
  url: string;
}

export interface Hashtag {
  id: number;
  name: string;
}

export interface PopularHashtagFacility {
  id: number;
  name: string;
  thumbnailImage: string;
}

export interface PopularHashtag {
  id: number;
  name: string;
  facilities: PopularHashtagFacility[];
}

export interface CreateReviewRequest {
  reservationId: number;
  content: string;
  starRating: number;
  images: string[];
  hashTagIds: number[];
}

export interface UpdateReviewRequest {
  content: string;
  starRating: number;
  images: string[];
  hashTagIds: number[];
}

export interface MyReview {
  id: number;
  content: string;
  starRating: number;
  images: string[];
  createdAt: string;
  facility: {
    name: string;
    imageUrl: string;
    building: {
      name: string;
    };
  };
}

export const reviewService = {
  getHashtags: async (): Promise<Hashtag[]> => {
    const response = await apiClient.get<Hashtag[]>('/hashtags');
    return response.data;
  },

  getPopularHashtags: async (size: number = 5): Promise<PopularHashtag[]> => {
    const response = await apiClient.get<PopularHashtag[]>('/hashtags/popular', {
      params: { size },
    });
    return response.data;
  },

  getReviewsByFacility: async (userId: number, page: number = 1): Promise<Review[]> => {
    const response = await apiClient.get<Review[]>(`/user/${userId}/reviews/byFacility`, {
      params: { page },
    });
    return response.data;
  },

  uploadReviewImages: async (files: File[]): Promise<string[]> => {
    const uploadPromises = files.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<ImageUploadResponse>('/reviews/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    });

    return Promise.all(uploadPromises);
  },

  createReview: async (reviewData: CreateReviewRequest): Promise<void> => {
    const response = await apiClient.post('/reviews', reviewData);
    return response.data;
  },

  getMyReviews: async (): Promise<MyReview[]> => {
    const response = await apiClient.get<MyReview[]>('/me/reviews');
    return response.data;
  },

  getReviewById: async (reviewId: number): Promise<MyReview> => {
    const response = await apiClient.get<MyReview>(`/reviews/${reviewId}`);
    return response.data;
  },

  updateReview: async (reviewId: number, reviewData: UpdateReviewRequest): Promise<void> => {
    const response = await apiClient.put(`/reviews/${reviewId}`, reviewData);
    return response.data;
  },

  deleteReview: async (reviewId: number): Promise<void> => {
    const response = await apiClient.delete(`/reviews/${reviewId}`);
    return response.data;
  },
};
